// !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
//
// This is the main entry point for the @zapier/zinnia-icons package.
//
// Anything you export from here can and will be used by product teams.
//
// !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!

import { ColorName } from './Colors';
import {
  allIcons,
  iconNameAliases,
  resolveIconName,
  type IconName,
} from './index-utils';
import styles from './index.module.css';
import { LiteralUnion } from './types/utils';
import { getRealSize, mergeClassNames } from './utils';

export type { IconName };

/**
 * An array of every icon name in Zinnia, including alternate-casing aliases
 * (see `iconNameAliases` in `index-utils.ts`).
 *
 * Please use `isZinniaIconName()` if you want to test if a string corresponds
 * to a Zinnia icon name.
 *
 * **Do not** use `iconNames.includes`, as it is much slower and doesn't provide
 * TypeScript safety.
 *
 * Useful if you want to provide a list of every supported icon. Notably useful
 * for Pagebuilder, which needs to let users select any icon supported by
 * Zinnia.
 *
 * @see {isIconName}
 * @example
 * <select>
 *   {iconNames.map((icon) => (
 *     <option key={icon}>{icon}</option>
 *   ))}
 * </select>
 */
export const iconNames: readonly IconName[] = Object.freeze(
  [
    ...(Object.keys(allIcons) as IconName[]),
    ...(Object.keys(iconNameAliases) as IconName[]),
  ].sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }))
);

/**
 * Returns `true` if the string is a Zinnia icon name, else `false`.
 *
 * Uses a TypeScript guard to promote the type from `string` to `IconName` so it
 * can be safely passed to the `<Icon>` component.
 *
 * @example
 * // name has type string
 * if (isIconName(name)) {
 *   // name has type IconName
 *   return <Icon name={name} />;
 * }
 */
export function isIconName(name: string): name is IconName {
  return iconNamesSet.has(name);
}

const iconNamesSet: ReadonlySet<string> = new Set<string>(iconNames);

/**
 * A Zinnia icon. Renders an inline `<svg>` with attached CSS `className` and
 * other attributes.
 *
 * **Don't forget to `import "@zapier/zinnia-icons/style.css"` for the styles to work.**
 *
 * @example
 * <Icon
 *    name="ratingHeart"
 *    color="PrimeRed"
 *    size="large"
 * />
 */
export function Icon({
  className,
  name,
  'aria-label': ariaLabel,
  // The old icon component just set this unconditionally to true, but that
  // feels like a huge opportunity make it easy to make inaccessible icons by
  // forgetting this prop. Let's use an intelligent default.
  'aria-hidden': ariaHidden = ariaLabel ? undefined : true,
  // Should this really be true by default? People shouldn't be making icons
  // directly clickable, right?
  canAcceptPointerEvents = true,
  color = 'inherit',
  isBlock = false,
  shouldAnimateFill = false,
  size = 'small',
}: IconProps) {
  // Don't add icon aliases here---read the README.md section on adding them.
  // We need aliases at the filesystem level in order to account for modular
  // icon imports. The casing-only aliases handled by `resolveIconName` are
  // the exception (see `iconNameAliases` in `index-utils.ts` for the macOS
  // filesystem reasoning).
  const Component = allIcons[resolveIconName(name)] ?? FallbackIcon;
  return (
    <Component
      // Extra special props handled by mapPropsToSvgProps (utils.ts), which
      // happens in the generated components (see build.mjs)
      size={size}
      color={color}
      // Used to render data-name and data-testid
      name={name}
      className={mergeClassNames(styles.icon, className)}
      // Accessibility props
      //
      // https://css-tricks.com/accessible-svgs/
      //
      // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-label
      //
      // mapPropsToSvgProps derives role and aria-hidden from the label, so we
      // only forward the label/hidden props here. Setting role unconditionally
      // here would override that derivation and produce contradictory ARIA
      // (aria-hidden + role="img") on decorative icons.
      aria-hidden={ariaHidden}
      aria-label={ariaLabel}
      // Legacy props from the old Icon component... We might want to mark these
      // as deprecated in the future, just to encourage developers to not use
      // them.
      data-block={isBlock}
      data-pointer-events={canAcceptPointerEvents}
      data-animate-fill={shouldAnimateFill}
    />
  );
}

/**
 * Props for the `<Icon>` component
 */
export type IconProps = {
  /**
   * Accessible label for the icon. Optional since in many cases the
   * parent node will already have an `ariaLabel` attached.
   */
  'aria-label'?: string;
  /**
   * Accessible attribute to hide the icon when the only role is presentation.
   * Optional since parent node can accept click events and the icon can have a aria-label. Default is true.
   */
  'aria-hidden'?: boolean;
  /**
   * Indicates whether `pointer-events` are accepted on this component.
   * `Icon` nodes can interfere with event firing, and disabling
   * `pointer-events` can prevent the interference. Generally the `Icon`
   * shouldn't accept events itself, but rather its parent should for
   * accessibility purposes.
   */
  canAcceptPointerEvents?: boolean;
  /**
   * Icon fill color. If this is not specified, the color will be inherited value of the CSS `color` property.
   */
  color?: ColorName;
  /**
   * Indicates whether the `Icon` is `display: block`, which removes
   * `line-height` related layout from it.
   */
  isBlock?: boolean;
  /**
   * String representing the name of icon to display. If the name is invalid, a square will be rendered instead.
   */
  name: IconName;
  /**
   * Whether or not the fill property should animate.
   * @default false
   */
  shouldAnimateFill?: boolean;
  /**
   * Size of the icon. This value will be the width and height of the icon, since it is a square.
   *
   * Acceptable values:
   * - small (24px), medium (36px), or large (48px)
   * - a number (in pixels)
   * - a string specifying css units
   */
  size?: LiteralUnion<'small' | 'medium' | 'large', number | string>;
  /**
   * Used to override styles on the icon
   */
  className?: string;
};

type BasicProps = {
  size?: number | string;
  className?: string;
  'aria-hidden'?: boolean;
  'aria-label'?: string;
  role?: string;
};

function FallbackIcon({
  size = 24,
  className,
  'aria-hidden': ariaHidden,
  'aria-label': ariaLabel,
  role,
}: BasicProps) {
  return (
    <svg
      role={role}
      aria-hidden={ariaHidden}
      aria-label={ariaLabel}
      className={className}
      data-is-missing-icon="true"
      viewBox="0 0 24 24"
      width={getRealSize(size)}
      height={getRealSize(size)}
    >
      <rect height="24" width="24" x="0" y="0" fill="currentColor" />
    </svg>
  );
}
